<template>
  <van-popup
    v-model="innerVisible"
    position="bottom"
    get-container="body"
    :close-on-click-overlay="false"
    class="seal-fullscreen-popup"
    :style="{ width: '100%', height: '100%' }"
    @closed="onClosed"
  >
    <div
      ref="touchHost"
      class="seal-fullscreen-host"
      @touchstart.prevent="onTouchStart"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div class="seal-fullscreen-top">
        <van-button size="small" round plain hairline native-type="button" @click.stop="close">关闭</van-button>
        <span class="seal-fullscreen-title">{{ title }}</span>
      </div>
      <canvas ref="markCanvas" class="seal-fullscreen-canvas" />
      <div class="seal-fullscreen-label">{{ label }}</div>
      <div v-if="previewPoints.length === 4" class="seal-fullscreen-preview">已采集 4 点</div>
    </div>
  </van-popup>
</template>

<script>
import { mapTouchToStandard } from '@/pages/utils/seal-area-presets'

export default {
  name: 'SealFullscreenTouch',
  props: {
    value: { type: Boolean, default: false },
    areaId: { type: String, required: true },
    title: { type: String, default: '全屏盖章' },
    label: { type: String, default: '四指同时按下屏幕' },
    /** 是否要求恰好 4 指才触发 confirm */
    requireFour: { type: Boolean, default: true }
  },
  data() {
    return {
      innerVisible: false,
      previewPoints: [],
      pendingPoints: null
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.innerVisible = v
        if (v) {
          this.previewPoints = []
          this.pendingPoints = null
          this.$nextTick(() => this.resizeCanvas())
        }
      }
    },
    innerVisible(v) {
      this.$emit('input', v)
    }
  },
  mounted() {
    this._onResize = () => {
      if (this.innerVisible) this.resizeCanvas()
    }
    window.addEventListener('resize', this._onResize)
    window.addEventListener('orientationchange', this._onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
    window.removeEventListener('orientationchange', this._onResize)
  },
  methods: {
    close() {
      this.innerVisible = false
    },
    onClosed() {
      this.previewPoints = []
      this.pendingPoints = null
      this.clearCanvas()
    },
    resizeCanvas() {
      const host = this.$refs.touchHost
      const cv = this.$refs.markCanvas
      if (!host || !cv) return
      const w = host.clientWidth
      const h = host.clientHeight
      if (w <= 0 || h <= 0) return
      const dpr = window.devicePixelRatio || 1
      cv.width = Math.round(w * dpr)
      cv.height = Math.round(h * dpr)
      cv.style.width = w + 'px'
      cv.style.height = h + 'px'
      const ctx = cv.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)
    },
    clearCanvas() {
      const host = this.$refs.touchHost
      const cv = this.$refs.markCanvas
      if (!host || !cv) return
      const ctx = cv.getContext('2d')
      ctx.clearRect(0, 0, host.clientWidth, host.clientHeight)
    },
    drawMarks(touches, rect) {
      this.clearCanvas()
      const cv = this.$refs.markCanvas
      if (!cv || !touches) return
      const ctx = cv.getContext('2d')
      for (let i = 0; i < touches.length; i++) {
        const t = touches[i]
        const px = t.clientX - rect.left
        const py = t.clientY - rect.top
        ctx.fillStyle = '#e53935'
        ctx.beginPath()
        ctx.arc(px, py, 10, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.font = 'bold 12px sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(i + 1), px, py)
      }
    },
    onTouchStart(e) {
      const host = this.$refs.touchHost
      if (!host || !e.touches || e.touches.length === 0) return
      this.resizeCanvas()
      const count = e.touches.length
      if (this.requireFour && count !== 4) {
        this.previewPoints = []
        this.pendingPoints = null
        this.clearCanvas()
        return
      }
      const rect = host.getBoundingClientRect()
      const points = []
      for (let i = 0; i < count; i++) {
        const mapped = mapTouchToStandard(e.touches[i], rect, this.areaId)
        points.push([mapped.standardX, mapped.standardY])
      }
      this.previewPoints = points
      this.pendingPoints = points
      this.drawMarks(e.touches, rect)
    },
    onTouchEnd(e) {
      if (e.touches && e.touches.length > 0) return
      const pts = this.pendingPoints
      this.pendingPoints = null
      if (!pts || pts.length === 0) return
      if (this.requireFour && pts.length !== 4) {
        this.previewPoints = []
        this.clearCanvas()
        return
      }
      this.$emit('confirm', pts)
    }
  }
}
</script>

<style scoped>
.seal-fullscreen-popup {
  border-radius: 0;
}

.seal-fullscreen-host {
  position: relative;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  touch-action: none;
  overflow: hidden;
}

.seal-fullscreen-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.seal-fullscreen-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top, 0));
  background: rgba(255, 255, 255, 0.92);
}

.seal-fullscreen-title {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.seal-fullscreen-label {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(24px + env(safe-area-inset-bottom, 0));
  text-align: center;
  font-size: 13px;
  color: #666;
  z-index: 2;
  pointer-events: none;
}

.seal-fullscreen-preview {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border-radius: 8px;
  font-size: 14px;
  z-index: 2;
  pointer-events: none;
}
</style>
